/**
 * Transmit rules: how long you may hold the key, and how often.
 *
 * These are **in-world mechanics, not rate limiting**. A handheld set has a duty cycle; a
 * transmitter that has been keyed three times in a row gets hot; a channel you monopolise is a
 * channel nobody else can answer on. Presenting the limits as physical facts about the equipment
 * — rather than as an app telling you to slow down — is what keeps them part of the fiction.
 *
 * They also do real work: an unlimited push-to-talk button is an unlimited call to the matcher
 * proxy, so the cheapest place to cap cost is in the player's hand, before a request exists.
 *
 * Pure and clock-injected, because "what happens at the boundary of the cooldown" is exactly the
 * sort of thing that is untestable if it reads `Date.now()` directly.
 */

export type TransmitPhase =
  /** Ready to key up. */
  | 'idle'
  /** Holding the key; audio is being captured. */
  | 'transmitting'
  /** Just released; too soon to key up again. */
  | 'cooldown'
  /** Too many transmissions back to back; a longer wait. */
  | 'overheated';

export interface TransmitLimits {
  /** Longest single transmission, ms. Capture is cut at this point. */
  maxTransmitMs: number;
  /** Wait after a normal release, ms. */
  cooldownMs: number;
  /** Consecutive transmissions allowed before the longer wait. */
  maxConsecutive: number;
  /** Wait once `maxConsecutive` is hit, ms. */
  overheatedCooldownMs: number;
  /**
   * Quiet time after which the consecutive counter forgets, ms. Without it, a player who
   * transmits three times over an hour would be treated the same as one who did it in ten
   * seconds, which is neither fair nor physical.
   */
  consecutiveResetMs: number;
}

/**
 * Defaults, chosen against the authored content rather than picked round.
 *
 * `maxTransmitMs` 8000 — the longest option in the sample mystery is about two seconds to say;
 * eight gives room to hesitate, restate, and still be inside a single Web Speech utterance.
 * `cooldownMs` 1500 — long enough to feel like equipment, short enough that a mishearing can be
 * corrected without the player feeling punished for it.
 * `maxConsecutive` 4 with a 12 s penalty — four rapid-fire attempts is someone struggling with
 * recognition, and the right response is a pause, not a lockout. Twelve seconds is roughly the
 * point where a player reads the room and tries the buttons instead.
 */
export const DEFAULT_TRANSMIT_LIMITS: TransmitLimits = {
  maxTransmitMs: 8_000,
  cooldownMs: 1_500,
  maxConsecutive: 4,
  overheatedCooldownMs: 12_000,
  consecutiveResetMs: 30_000,
};

export interface TransmitState {
  phase: TransmitPhase;
  /** True when the player may key up right now. */
  canTransmit: boolean;
  /** Ms remaining in the current transmission, when transmitting. */
  remainingMs: number;
  /** Ms until transmitting is allowed again, when cooling down. */
  waitMs: number;
  /** Transmissions in the current burst. */
  consecutive: number;
}

export interface TransmitRulesOptions {
  limits?: Partial<TransmitLimits>;
  /** Injectable clock. */
  nowMs?: () => number;
}

/**
 * The state machine. Ask it whether the player may transmit, tell it when they key up and
 * release, and read `state()` to render the UI.
 */
export class TransmitRules {
  readonly limits: TransmitLimits;

  private readonly nowMs: () => number;
  private startedAt: number | null = null;
  private blockedUntil = 0;
  private lastEndedAt = 0;
  private consecutive = 0;
  private overheated = false;

  constructor(options: TransmitRulesOptions = {}) {
    this.limits = { ...DEFAULT_TRANSMIT_LIMITS, ...options.limits };
    this.nowMs = options.nowMs ?? (() => Date.now());
  }

  /** Current state, derived rather than stored — there is no tick to miss. */
  state(): TransmitState {
    const now = this.nowMs();

    if (this.startedAt !== null) {
      const elapsed = now - this.startedAt;
      return {
        phase: 'transmitting',
        canTransmit: false,
        remainingMs: Math.max(0, this.limits.maxTransmitMs - elapsed),
        waitMs: 0,
        consecutive: this.consecutive,
      };
    }

    const waitMs = Math.max(0, this.blockedUntil - now);
    if (waitMs > 0) {
      return {
        phase: this.overheated ? 'overheated' : 'cooldown',
        canTransmit: false,
        remainingMs: 0,
        waitMs,
        consecutive: this.consecutive,
      };
    }

    return {
      phase: 'idle',
      canTransmit: true,
      remainingMs: 0,
      waitMs: 0,
      consecutive: this.effectiveConsecutive(now),
    };
  }

  canTransmit(): boolean {
    return this.state().canTransmit;
  }

  /**
   * Key up. Returns false when the rules refuse — the caller must not start capturing.
   */
  begin(): boolean {
    const now = this.nowMs();
    if (!this.state().canTransmit) return false;

    // A long enough silence means this is a fresh burst, not a continuation.
    this.consecutive = this.effectiveConsecutive(now) + 1;
    this.overheated = false;
    this.startedAt = now;
    return true;
  }

  /**
   * Release. Called on key-up and also when `maxTransmitMs` cuts the transmission short; the
   * consequences are identical, which is the point — running out of time is not a punishment.
   */
  end(): TransmitState {
    if (this.startedAt === null) return this.state();

    const now = this.nowMs();
    this.startedAt = null;
    this.lastEndedAt = now;

    const hitLimit = this.consecutive >= this.limits.maxConsecutive;
    this.overheated = hitLimit;
    this.blockedUntil =
      now + (hitLimit ? this.limits.overheatedCooldownMs : this.limits.cooldownMs);
    if (hitLimit) this.consecutive = 0;

    return this.state();
  }

  /** True once the current transmission has run out of time. Polled by the capture loop. */
  hasExpired(): boolean {
    if (this.startedAt === null) return false;
    return this.nowMs() - this.startedAt >= this.limits.maxTransmitMs;
  }

  /** Clear all limits. For leaving a conversation, where holding a cooldown would be nonsense. */
  reset(): void {
    this.startedAt = null;
    this.blockedUntil = 0;
    this.consecutive = 0;
    this.overheated = false;
    this.lastEndedAt = 0;
  }

  /** The consecutive count, forgetting a burst that has gone quiet. */
  private effectiveConsecutive(now: number): number {
    if (this.lastEndedAt === 0) return this.consecutive;
    return now - this.lastEndedAt >= this.limits.consecutiveResetMs ? 0 : this.consecutive;
  }
}
